import styled, { css } from 'styled-components'
import { Tabs as CheggUiTabs } from '@chegg-ui/tabs'
import { font, spacing, colors } from '@chegg-ui/theme'
import { FeedbackTab } from '@chegg-wt/core-wa/constants/paraphraser'
import { ToastType } from '@chegg-wt/core-wa/state/types'
import { TOAST_ERRORS } from './constants'

interface TabsProps {
  grammarCardsCount: number
  clarityCardsCount: number
  disabled?: boolean
  paperId?: string
  isPaperCheckInProgress?: boolean
  toastType?: ToastType | null
  requiresRecheck: boolean
}
export const MAX_FEEDBACK_COUNT_PLUS_ON_TAB = '999+'
export const TICK_HTML_CHARACTER = '\\2713'
export const MAX_FEEDBACK_COUNT_AS_IT_IS = 999
const GRAMMAR_GREEN = '#1DA275'
const CLARITY_BLUE = '#2E8AEE'

export const getBeforeContentCss = (tabCircleContent) => {
  return css`
    content: '${tabCircleContent}';
    ${tabCircleContent === MAX_FEEDBACK_COUNT_PLUS_ON_TAB &&
    `font-size: ${font.size.px12}`};
  `
}

export const getTabValue = (
  activeTabIndex,
  grammarCardsCount,
  clarityCardsCount,
  isPaperCheckInProgress,
  toastType,
  isNonActiveTab
) => {
  if (activeTabIndex === FeedbackTab.NO_ACTIVE_TAB) return getBeforeContentCss(0)

  let count =
    activeTabIndex === FeedbackTab.GRAMMAR_TAB ? grammarCardsCount : clarityCardsCount

  if (isNonActiveTab)
    count =
      activeTabIndex === FeedbackTab.PARAPHRASE_TAB
        ? grammarCardsCount
        : clarityCardsCount

  const countInRange =
    count <= MAX_FEEDBACK_COUNT_AS_IT_IS ? count : MAX_FEEDBACK_COUNT_PLUS_ON_TAB

  // if in progress or error, don't show TICK_HTML_CHARACTER
  if (isPaperCheckInProgress || TOAST_ERRORS.includes(toastType))
    return getBeforeContentCss(countInRange)

  return getBeforeContentCss(countInRange || TICK_HTML_CHARACTER)
}

const getNonActiveBackgroundColor = (
  activeTabIndex: FeedbackTab,
  grammarCardsCount: number,
  clarityCardsCount: number,
  disabled: boolean,
  requiresRecheck: boolean
) => {
  if ((disabled && requiresRecheck) || activeTabIndex === FeedbackTab.NO_ACTIVE_TAB)
    return colors.grey100
  if (activeTabIndex === FeedbackTab.PARAPHRASE_TAB) {
    return grammarCardsCount === 0 ? GRAMMAR_GREEN : 'white'
  }
  return clarityCardsCount === 0 ? CLARITY_BLUE : 'white'
}

const getNonActiveTabColor = (
  activeTabIndex: FeedbackTab,
  grammarCardsCount: number,
  clarityCardsCount: number
) => {
  if (activeTabIndex === FeedbackTab.NO_ACTIVE_TAB) return colors.dark
  if (activeTabIndex === FeedbackTab.PARAPHRASE_TAB) {
    return grammarCardsCount === 0 ? 'white' : 'dark'
  }
  return clarityCardsCount === 0 ? 'white' : 'dark'
}

export const TabsContainer = styled.div`
  position: relative;
`
export const LoaderContainer = styled.div`
  position: absolute;
  right: ${font.size.px32};
  top: 5px;
`

export const Tabs = styled(CheggUiTabs)<TabsProps>`
  &&& {
    > div > ul {
      border-bottom: unset;
      padding: 0 ${spacing.px16};

      > li {
        // tab
        margin-right: ${spacing.px8};
        font-size: ${font.size.px14};
        padding: 0 ${spacing.px8} 0 0;
        > a {
          pointer-events: ${({ disabled = false }) => (disabled ? 'none' : 'auto')};
          border-radius: ${spacing.px24};
          outline: none;

          &:hover {
            text-decoration: none;
          }

          &::after {
            content: none;
          }

          &::before {
            position: absolute;
            left: 3px;
            top: 50%;
            transform: translateY(-50%);
            width: ${spacing.px32};
            height: ${spacing.px32};
            border-radius: 50%;
            background-color: ${({ disabled = false }) =>
              disabled ? colors.grey100 : colors.white};
            border: 0.5px solid ${colors.grey200};
            font-weight: bold;
            font-size: ${font.size.px14};
            display: flex;
            align-items: center;
            justify-content: center;
          }

          padding: ${spacing.px8} ${spacing.px12} ${spacing.px8} ${spacing.px40};
          &[aria-selected='true'] {
            // active tab
            background-color: ${colors.grey100};
            ::before {
              ${({
                activeTabIndex,
                grammarCardsCount,
                clarityCardsCount,
                isPaperCheckInProgress,
                toastType,
              }) =>
                getTabValue(
                  activeTabIndex,
                  grammarCardsCount,
                  clarityCardsCount,
                  isPaperCheckInProgress,
                  toastType,
                  false
                )};
              background-color: ${({ activeTabIndex }) =>
                activeTabIndex === 0 ? GRAMMAR_GREEN : CLARITY_BLUE};
              color: ${colors.white};

              // override the font-family for tick to make it look as per figma
              ${({ grammarCardsCount, clarityCardsCount }) =>
                (grammarCardsCount === 0 || clarityCardsCount === 0) &&
                css`
                  font-family: 'system-ui';
                `}
            }
          }

          // for non active tab
          :not([aria-selected='true']) {
            ::before {
              ${({
                activeTabIndex,
                grammarCardsCount,
                clarityCardsCount,
                isPaperCheckInProgress,
                toastType,
              }) =>
                getTabValue(
                  activeTabIndex,
                  grammarCardsCount,
                  clarityCardsCount,
                  isPaperCheckInProgress,
                  toastType,
                  true
                )};
            }

            ${({
              grammarCardsCount,
              clarityCardsCount,
              paperId,
              activeTabIndex,
              disabled,
              requiresRecheck,
            }) =>
              paperId &&
              (grammarCardsCount === 0 || clarityCardsCount === 0) &&
              css`
                font-weight: bold;
                ::before {
                  background-color: ${getNonActiveBackgroundColor(
                    activeTabIndex,
                    grammarCardsCount,
                    clarityCardsCount,
                    disabled,
                    requiresRecheck
                  )};
                  color: ${getNonActiveTabColor(
                    activeTabIndex,
                    grammarCardsCount,
                    clarityCardsCount
                  )};
                  // override the font-family for tick to make it look as per figma
                  font-family: 'system-ui';
                }
              `}
          }
        }
      }
    }
    display: flex;
    margin-top: ${spacing.px16};
  }
`
